import { ref, computed } from 'vue'
import Provider from '@/api/Provider.js'
import { FORM_MESSAGES } from '@/constants/providerOptions.js'

export function useProviderForm() {
  // Form data
  const formData = ref({
    name: '',
    display_name: '',
    description: '',
    website_url: '',
    country_code: '',
    provider_type: '',
    thumbnail_url: '',
    categories: []
  })

  // Form state
  const isSubmitting = ref(false)
  const status = ref(null) // null, 'success', 'error'
  const statusMessage = ref('')

  // Form validation
  const isValid = computed(() => {
    return formData.value.name.trim() !== '' &&
           formData.value.display_name.trim() !== ''
  })

  const modalTitle = computed(() => (mode) => {
    return mode === 'create' ? 'Create New Provider' : 'Edit Provider Profile'
  })

  const submitButtonText = computed(() => (mode) => {
    if (isSubmitting.value) {
      return mode === 'create' ? 'Creating...' : 'Updating...'
    }
    return mode === 'create' ? 'Create Provider' : 'Update Provider'
  })

  // Reset form function
  const resetForm = () => {
    formData.value = {
      name: '',
      display_name: '',
      description: '',
      website_url: '',
      country_code: '',
      provider_type: '',
      thumbnail_url: '',
      categories: []
    }
    status.value = null
    statusMessage.value = ''
    isSubmitting.value = false
  }

  // URL validation
  const validateUrl = (url) => {
    if (!url) return true // Optional field
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  // Fetch provider data for edit mode
  const fetchProviderData = async (providerName) => {
    try {
      const result = await Provider.getProvider(providerName)
      if (result && result.name) {
        formData.value = {
          name: result.name || '',
          display_name: result.display_name || '',
          description: result.description || '',
          website_url: result.website_url || '',
          country_code: result.country_code || '',
          provider_type: result.provider_type || '',
          thumbnail_url: result.thumbnail_url || '',
          categories: result.categories || []
        }
        return result
      }
    } catch (error) {
      console.error('Error fetching provider data:', error)
      throw error
    }
  }

  // Prepare form data for API submission
  // Convert empty strings to null for optional fields
  const prepareFormData = () => {
    const data = {
      name: formData.value.name.trim(),
      display_name: formData.value.display_name.trim()
    }

    // Only include optional fields if they have values
    if (formData.value.description && formData.value.description.trim() !== '') {
      data.description = formData.value.description.trim()
    }

    if (formData.value.website_url && formData.value.website_url.trim() !== '') {
      data.website_url = formData.value.website_url.trim()
    }

    if (formData.value.country_code && formData.value.country_code.trim() !== '') {
      data.country_code = formData.value.country_code.trim()
    }

    if (formData.value.provider_type && formData.value.provider_type.trim() !== '') {
      data.provider_type = formData.value.provider_type.trim()
    }

    return data
  }

  // Submit form logic
  const submitForm = async (mode, providerData, thumbnailFile, onSuccess) => {
    if (!isValid.value) return

    // Validate URL if provided
    if (formData.value.website_url && !validateUrl(formData.value.website_url)) {
      status.value = 'error'
      statusMessage.value = FORM_MESSAGES.INVALID_URL
      return
    }

    isSubmitting.value = true
    status.value = null

    try {
      let result
      let providerName = formData.value.name

      if (mode === 'create') {
        // For create mode, create provider first, then upload thumbnail
        const cleanedData = prepareFormData()
        result = await Provider.createProvider(cleanedData)

        if (result.status === 'success') {
          // If we have a thumbnail file, upload it after successful creation
          if (thumbnailFile) {
            try {
              const uploadResult = await Provider.uploadThumbnail(providerName, thumbnailFile)
              if (uploadResult.status === 'success' && uploadResult.data?.thumbnail_url) {
                // Update the result data with the thumbnail URL
                result.data = { ...result.data, thumbnail_url: uploadResult.data.thumbnail_url }
              }
            } catch (uploadError) {
              console.warn('Thumbnail upload failed after creation:', uploadError)
              // Provider is created successfully, but thumbnail failed - still consider success
            }
          }

          // Update categories if any selected
          if (formData.value.categories && formData.value.categories.length > 0) {
            try {
              await Provider.updateProviderCategories(providerName, formData.value.categories)
            } catch (categoryError) {
              console.warn('Category update failed after creation:', categoryError)
              // Provider is created successfully, but category update failed - still consider success
            }
          }
        }
      } else {
        // For edit mode, use the original provider name for API calls
        const originalProviderName = providerData.name || formData.value.name

        // If we have a new thumbnail file, upload it first
        if (thumbnailFile) {
          try {
            const uploadResult = await Provider.uploadThumbnail(originalProviderName, thumbnailFile)
            if (uploadResult.status === 'success' && uploadResult.data?.thumbnail_url) {
              formData.value.thumbnail_url = uploadResult.data.thumbnail_url
            }
          } catch (uploadError) {
            console.warn('Thumbnail upload failed during update:', uploadError)
            // Continue with update even if thumbnail fails
          }
        }

        const cleanedData = prepareFormData()
        result = await Provider.updateProvider(originalProviderName, cleanedData)

        // Update categories
        if (result.status === 'success') {
          try {
            await Provider.updateProviderCategories(originalProviderName, formData.value.categories || [])
          } catch (categoryError) {
            console.warn('Category update failed:', categoryError)
            // Provider is updated successfully, but category update failed - still consider success
          }
        }
      }

      if (result.status === 'success') {
        status.value = 'success'
        if (onSuccess) {
          onSuccess(result.data || formData.value)
        }
      } else {
        status.value = 'error'
        statusMessage.value = result.message || 'An error occurred'
      }

      return result
    } catch (error) {
      status.value = 'error'
      statusMessage.value = 'An unexpected error occurred'
      console.error('Form submission error:', error)
      throw error
    } finally {
      isSubmitting.value = false
    }
  }

  return {
    // State
    formData,
    isSubmitting,
    status,
    statusMessage,

    // Computed
    isValid,
    modalTitle,
    submitButtonText,

    // Methods
    resetForm,
    validateUrl,
    fetchProviderData,
    submitForm
  }
}